import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Eye,
  CheckCircle2,
  Loader2,
  AlertCircle,
  RefreshCw,
  Download,
  ArrowLeft,
  Search,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Match FieldReports.js kind colors so the two pages read the same.
const KIND_BADGE = {
  PreCon: 'bg-blue-100 text-blue-700',
  Locate: 'bg-purple-100 text-purple-700',
  'Soft Dig': 'bg-orange-100 text-orange-700',
};

// Preview route mapping (new-style HTML PDF that users can Print/Save).
// We navigate to these instead of pulling the legacy backend-generated PDF
// so the Review Queue PDF action produces the SAME format as the approved
// reports list.
const PREVIEW_ROUTE = {
  PreCon: (id) => `/precon/${id}/preview`,
  Locate: (id) => `/locate/${id}/preview`,
  'Soft Dig': (id) => `/soft-dig/${id}/preview`,
};

// Inline "Ready / X/Y photos" indicator — same component shape as the
// PhotoReadinessDot on FieldReports.js so the two pages read identically.
function PhotoReadinessDot({ record }) {
  if (!record) return null;
  const exp = Number(record.expected_photos_count) || 0;
  const rec = Number(record.received_photos_count) || 0;
  // No photo metadata recorded yet → don't show a dot at all (keeps the
  // row clean for older reports created before photo-counting shipped).
  if (exp === 0 && rec === 0 && record.photos_ready === undefined) return null;
  const ready = record.photos_ready === true || (exp > 0 && rec >= exp);
  if (ready) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-green-700 flex-shrink-0"
        title="All photos uploaded — ready to review"
      >
        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
        Ready
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-amber-700 flex-shrink-0"
      title={`${rec} of ${exp} photos uploaded — wait before approving`}
    >
      <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse inline-block" />
      {rec}/{exp} photos
    </span>
  );
}

export default function ReviewQueue() {
  const navigate = useNavigate();
  const { token, isAdmin, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({ total: 0, precon: 0, locate: 0, soft_dig: 0 });
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');

  const canApprove = isAdmin() || hasPermission('can_approve_field_reports');

  useEffect(() => {
    if (!canApprove) {
      toast.error('Access denied. You do not have permission to approve field reports.');
      navigate('/dashboard');
      return;
    }
    loadReviewQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReviewQueue = async () => {
    setLoading(true);
    try {
      const [reportsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/field-reports/review-queue`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/api/field-reports/review-queue/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setReports(reportsRes.data || []);
      setStats(statsRes.data || { total: 0, precon: 0, locate: 0, soft_dig: 0 });
    } catch (error) {
      console.error('Failed to load review queue:', error);
      toast.error('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  };

  // PDF action: open the new-style HTML preview page in a new tab so the
  // user can Print/Save as PDF using the unified report layout (same one
  // shown for Approved reports). Old behaviour pulled the legacy backend
  // PDF which used a different visual style and was confusing.
  const handleOpenPreview = (report) => {
    const path = PREVIEW_ROUTE[report.form_type]?.(report.id);
    if (!path) {
      toast.error('Unknown form type');
      return;
    }
    window.open(path, '_blank', 'noopener');
  };

  // Title in the same style as FieldReports: "<JobNumber> - <JobTitle>" with
  // a fallback to address/project_name when job_title is missing.
  const getTitle = (r) => {
    const jn = r.job_number || '';
    const t =
      r.job_title ||
      r.project_name ||
      r.site_location ||
      r.address ||
      'Untitled report';
    return jn ? `${jn} - ${t}` : t;
  };

  const getMeta = (r) => {
    const submitter =
      r.submitted_by ||
      r.assigned_team ||
      r.cll_lead_on_site ||
      r.user_name ||
      '';
    let dateLabel = '';
    const d = r.submitted_at || r.updated_at || r.created_at;
    if (d) {
      try {
        dateLabel = format(new Date(d), 'MMM d, yyyy h:mm a');
      } catch {
        dateLabel = '';
      }
    }
    const location =
      r.site_location || r.address || r.project_name || '';
    return [submitter, dateLabel, location].filter(Boolean).join(' • ');
  };

  const getFilteredReports = () => {
    let list = reports;
    if (activeTab === 'precon') list = list.filter((r) => r.form_type === 'PreCon');
    else if (activeTab === 'locate') list = list.filter((r) => r.form_type === 'Locate');
    else if (activeTab === 'softdig') list = list.filter((r) => r.form_type === 'Soft Dig');
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase();
      list = list.filter(
        (r) =>
          (r.job_number || '').toLowerCase().includes(t) ||
          (r.job_title || '').toLowerCase().includes(t) ||
          (r.site_location || '').toLowerCase().includes(t) ||
          (r.address || '').toLowerCase().includes(t) ||
          (r.project_name || '').toLowerCase().includes(t) ||
          (r.submitted_by || '').toLowerCase().includes(t) ||
          (r.cll_lead_on_site || '').toLowerCase().includes(t),
      );
    }
    const ts = (r) =>
      new Date(r.submitted_at || r.updated_at || r.created_at || 0).getTime();
    if (sortBy === 'date_desc') list = [...list].sort((a, b) => ts(b) - ts(a));
    else if (sortBy === 'date_asc') list = [...list].sort((a, b) => ts(a) - ts(b));
    else if (sortBy === 'type')
      list = [...list].sort((a, b) => (a.form_type || '').localeCompare(b.form_type || ''));
    return list;
  };

  // Single compact row — mirrors the layout used by FieldReports.js
  // (purple/blue/orange kind badge → "Pending" status badge → readiness dot
  // → title + meta → ghost icon actions on the right). Clicking the row
  // navigates to the form to sign & approve.
  const ReviewRow = ({ report }) => {
    const kindColor = KIND_BADGE[report.form_type] || 'bg-slate-100 text-slate-700';
    return (
      <div
        className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
        onClick={() => navigate(report.form_route)}
        data-testid={`review-row-${report.id}`}
      >
        <Badge className={`${kindColor} flex-shrink-0 w-24 justify-center whitespace-nowrap`}>
          {report.form_type}
        </Badge>
        <Badge className="bg-blue-100 text-blue-700 flex-shrink-0 w-20 justify-center">
          Pending
        </Badge>
        <PhotoReadinessDot record={report} />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 truncate">{getTitle(report)}</p>
          <p className="text-xs text-slate-500 truncate">{getMeta(report)}</p>
        </div>
        <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(report.form_route)}
            title="Open to Sign & Approve"
            data-testid={`view-row-${report.id}`}
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleOpenPreview(report)}
            title="Print / Save PDF (new format)"
            data-testid={`pdf-row-${report.id}`}
          >
            <Download className="w-4 h-4 text-amber-600" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(report.form_route)}
            title="Sign & Approve"
            data-testid={`sign-row-${report.id}`}
          >
            <CheckCircle2 className="w-4 h-4 text-green-600" />
          </Button>
        </div>
      </div>
    );
  };

  if (!canApprove) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
        <p className="text-slate-500">You don't have permission to access the review queue.</p>
      </div>
    );
  }

  const filtered = getFilteredReports();

  return (
    <div className="space-y-6">
      {/* Header — matches FieldReports.js title block. */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate('/field-reports')}
            className="p-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Review Queue</h1>
            <p className="text-slate-500">Field reports pending your review and approval</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={loadReviewQueue}
          disabled={loading}
          data-testid="refresh-queue-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Compact tab strip matching FieldReports — same shape, same colors. */}
      <div className="flex flex-wrap gap-2">
        {[
          { value: 'all', label: `All (${stats.total})` },
          { value: 'precon', label: `PreCon (${stats.precon})` },
          { value: 'locate', label: `Locate (${stats.locate})` },
          { value: 'softdig', label: `Soft Dig (${stats.soft_dig})` },
        ].map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setActiveTab(tab.value)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
              activeTab === tab.value
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
            data-testid={`tab-${tab.value}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search + sort — same toolbar as FieldReports. */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search job #, location, submitter..."
            className="pl-9"
            data-testid="review-queue-search"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full md:w-[180px]" data-testid="review-queue-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date_desc">Newest first</SelectItem>
            <SelectItem value="date_asc">Oldest first</SelectItem>
            <SelectItem value="type">By type</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Single Card wrapper holding all rows — same shell as FieldReports. */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              <span className="ml-3 text-slate-500">Loading review queue...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">All caught up!</h3>
              <p className="text-slate-500">No field reports pending review.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((report) => (
                <ReviewRow key={`${report.form_type}-${report.id}`} report={report} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
